import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { closeModal, openModal, useModals } from "@mantine/modals";
import { MdDelete } from "react-icons/md";
import { MESSAGES } from "src/utils/constants";
import { useToggle } from "@mantine/hooks";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaCheckCircle } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";
import { useCallback } from "react";

export const openActivateModal = (
  id: string,
  isActive: boolean,
  onUpdate: (id: string, isActive: boolean) => void,
  modalId = "activateModal"
) =>
  openModal({
    closeOnClickOutside: false,
    modalId: modalId,
    title: "Confirmation",
    centered: true,
    children: (
      <>
        <Stack spacing={15}>
          <Text size="sm">
            Are you sure you want to {isActive ? "deactivate" : "activate"}?
          </Text>
          <Group position="apart" spacing={10}>
            <Button
              fullWidth
              onClick={() => closeModal(modalId)}
              variant="outline"
            >
              {"Cancel"}
            </Button>
            <Button
              fullWidth
              onClick={() => {
                onUpdate(id, !isActive);
                closeModal(modalId);
              }}
            >
              {isActive ? "Deactivate" : "Activate"}
            </Button>
          </Group>
        </Stack>
      </>
    ),
  });

export default function useCustomModal() {
  const modals = useModals();
  const theme = useMantineTheme();
  const [refetchTableData, setrefetchTableData] = useToggle();

  const openDeleteModal = (id: string, onDelete: (id: string) => void) =>
    modals.openContextModal("delete", {
      closeOnClickOutside: false,
      title: MESSAGES.CONFIRM_DEL,
      centered: true,
      innerProps: {
        onDelete: () => onDelete(id),
      },
    });

  const DeleteRowIcon = useCallback(
    ({ id, onDelete }: { id: string; onDelete: (id: string) => void }) => {
      if (!id) return <></>;
      return (
        <ActionIcon
          onClick={() => openDeleteModal(id, onDelete)}
          style={{
            position: "initial",
          }}
        >
          <MdDelete color={`${theme.colors.red[6]}`} size={23} />
        </ActionIcon>
      );
    },
    []
  );

  const ShowActiveStatus = (isActive: boolean) => {
    return (
      <>
        {isActive ? (
          <FaCheckCircle size={24} color="green" />
        ) : (
          <IoMdCloseCircle size={28} color="red" />
        )}
      </>
    );
  };

  const GridOptions = useCallback(
    ({
      id,
      isActive,
      onUpdate,
      onDelete,
    }: {
      id: string;
      isActive: boolean;
      onUpdate: (id: string, isActive: boolean) => void;
      onDelete?: (id: string) => void;
    }) => {
      if (!id) return <></>;
      return (
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon style={{ position: "static" }}>
              <HiOutlineDotsVertical size={20} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Options</Menu.Label>
            <Menu.Item
              onClick={() => openActivateModal(id, isActive, onUpdate)}
            >
              <Text sx={{ fontWeight: 500 }}>
                {isActive ? "Deactivate" : "Activate"}
              </Text>
            </Menu.Item>
            {onDelete && (
              <Menu.Item
                icon={<MdDelete size={20} color="red" />}
                onClick={() => openDeleteModal(id, onDelete)}
              >
                <Text sx={{ fontWeight: 500 }}>Delete</Text>
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      );
    },
    []
  );

  return {
    openDeleteModal,
    DeleteRowIcon,
    refetchTableData,
    setrefetchTableData,
    GridOptions,
    ShowActiveStatus,
  };
}
